import { useRef, useEffect, useCallback } from 'react';

import { BrandConfig, BrandThreshold } from '../types';
import { parseEventValue, generateCombinationKey } from '../utils/eventValueUtils';

/* eslint-disable no-unused-vars */
interface UseAutoRowCreationProps {
  brandConfig: BrandConfig;
  selectedEvents: string[];
  pixels: Array<{ pixel_id: string; pixel_name: string }>;
  getEventsForThreshold: (threshold: BrandThreshold) => Array<{ id: number; event_name: string }>;
  onAddThresholdRow: (brandId: number) => void;
  onRemoveThresholdRow: (brandId: number, thresholdIndex: number) => void;
  onUpdateThreshold: (
    brandId: number,
    thresholdIndex: number,
    field: string,
    value: string | number
  ) => void;
  onPixelSelection: (brandId: number, thresholdIndex: number, pixelId: string) => Promise<void>;
  defaultOperator: '<' | '>' | '<=' | '>=' | '==' | '!=';
  defaultThreshold: number;
}

interface RowCreationData {
  pixelId: string;
  pixelName: string;
  eventId: number;
  eventName: string;
  operator: '<' | '>' | '<=' | '>=' | '==' | '!=';
  threshold: number;
  combinationKey: string;
}
/* eslint-enable no-unused-vars */

/**
 * Hook to handle automatic row creation when events are selected
 * Separates this complex logic from the main component
 */
export const useAutoRowCreation = ({
  brandConfig,
  selectedEvents,
  pixels,
  getEventsForThreshold,
  onAddThresholdRow,
  onRemoveThresholdRow,
  onUpdateThreshold,
  onPixelSelection,
  defaultOperator,
  defaultThreshold,
}: UseAutoRowCreationProps) => {
  // Track which combinations have been auto-created to avoid duplicates
  const autoCreatedCombinationsRef = useRef<Set<string>>(new Set());

  // Track which threshold IDs were auto-created for which combination keys
  const autoCreatedThresholdsRef = useRef<Map<string, string[]>>(new Map());

  // Track pending row creations that need to be populated after rows are added
  const pendingCreationsRef = useRef<RowCreationData[]>([]);

  // Track threshold count before creating new rows
  const thresholdCountBeforeCreationRef = useRef<number>(0);

  // Track previous selected events to detect removals
  const prevSelectedEventsRef = useRef<string[]>(selectedEvents);

  /**
   * Check if a row with this event already exists
   */
  const rowExists = useCallback(
    (eventId: number, pixelId?: string): boolean => {
      return brandConfig.thresholds.some(
        t => t.eventId === eventId && (!pixelId || t.pixelId === pixelId)
      );
    },
    [brandConfig.thresholds]
  );

  /**
   * Find pixel information for an event
   */
  const findPixelForEvent = useCallback(
    (eventId: number, pixelIdFromValue?: string): { pixelId: string; pixelName: string } => {
      // If pixelId is provided in the event value, use it
      if (pixelIdFromValue) {
        const pixel = pixels.find(p => p.pixel_id === pixelIdFromValue);
        if (pixel) {
          return { pixelId: pixel.pixel_id, pixelName: pixel.pixel_name };
        }
      }

      // Otherwise, search for the pixel that has this event
      for (const pixel of pixels) {
        const tempThreshold: BrandThreshold = {
          id: 'temp-check',
          pixelId: pixel.pixel_id,
          threshold: 0,
          operator: '<',
        };
        const events = getEventsForThreshold(tempThreshold);
        if (events.some(e => e.id === eventId)) {
          return { pixelId: pixel.pixel_id, pixelName: pixel.pixel_name };
        }
      }

      return { pixelId: '', pixelName: '' };
    },
    [pixels, getEventsForThreshold]
  );

  /**
   * Fill an existing empty row with data
   */
  const fillEmptyRow = useCallback(
    (rowData: RowCreationData, thresholdIndex: number) => {
      const threshold = brandConfig.thresholds[thresholdIndex];
      onUpdateThreshold(brandConfig.brandId, thresholdIndex, 'pixelId', rowData.pixelId);
      onUpdateThreshold(brandConfig.brandId, thresholdIndex, 'pixelName', rowData.pixelName);
      onUpdateThreshold(brandConfig.brandId, thresholdIndex, 'eventId', rowData.eventId);
      onUpdateThreshold(brandConfig.brandId, thresholdIndex, 'eventName', rowData.eventName);
      onUpdateThreshold(brandConfig.brandId, thresholdIndex, 'operator', rowData.operator);
      onUpdateThreshold(brandConfig.brandId, thresholdIndex, 'threshold', rowData.threshold);

      // Track this threshold as auto-created
      if (threshold?.id) {
        const existingIds = autoCreatedThresholdsRef.current.get(rowData.combinationKey) || [];
        if (!existingIds.includes(threshold.id)) {
          autoCreatedThresholdsRef.current.set(rowData.combinationKey, [
            ...existingIds,
            threshold.id,
          ]);
        }
      }

      // Trigger pixel selection to ensure events are fetched
      if (rowData.pixelId) {
        onPixelSelection(brandConfig.brandId, thresholdIndex, rowData.pixelId);
      }
    },
    [brandConfig, onUpdateThreshold, onPixelSelection]
  );

  /**
   * Handle removal of auto-created rows when events are deselected
   */
  const removeAutoCreatedRows = useCallback(
    (removedEvents: string[]) => {
      const thresholdIdsToRemove = new Set<string>();

      removedEvents.forEach(eventValue => {
        const parsed = parseEventValue(eventValue);
        if (!parsed) return;

        const combinationKey = generateCombinationKey(parsed.eventId, parsed.pixelId);
        const trackedIds = autoCreatedThresholdsRef.current.get(combinationKey) || [];
        trackedIds.forEach(id => thresholdIdsToRemove.add(id));

        // Remove from tracking maps
        autoCreatedThresholdsRef.current.delete(combinationKey);
        autoCreatedCombinationsRef.current.delete(combinationKey);

        // Remove any other rows (manually created) that match the same event/pixel
        brandConfig.thresholds.forEach(threshold => {
          if (!threshold.id || !threshold.eventId) return;
          const eventMatches = threshold.eventId === parsed.eventId;
          const pixelMatches = parsed.pixelId ? threshold.pixelId === parsed.pixelId : true;

          if (eventMatches && pixelMatches) {
            thresholdIdsToRemove.add(threshold.id);
          }
        });
      });

      if (thresholdIdsToRemove.size === 0) {
        return;
      }

      // Calculate indices and remove in reverse order
      const indicesToRemove = Array.from(thresholdIdsToRemove)
        .map(thresholdId => brandConfig.thresholds.findIndex(t => t.id === thresholdId))
        .filter(index => index !== -1)
        .sort((a, b) => b - a);

      indicesToRemove.forEach(thresholdIndex => {
        onRemoveThresholdRow(brandConfig.brandId, thresholdIndex);
      });
    },
    [brandConfig, onRemoveThresholdRow]
  );

  /**
   * Create rows for selected events
   */
  const createRowsForEvents = useCallback(() => {
    if (selectedEvents.length === 0) {
      return;
    }

    const combinationsToCreate: RowCreationData[] = [];
    const seenCombinations = new Set<string>();

    selectedEvents.forEach(eventValue => {
      const parsed = parseEventValue(eventValue);
      if (!parsed) return;

      const combinationKey = generateCombinationKey(parsed.eventId, parsed.pixelId);

      // Skip if already processed or already auto-created
      if (
        seenCombinations.has(combinationKey) ||
        autoCreatedCombinationsRef.current.has(combinationKey)
      ) {
        return;
      }

      seenCombinations.add(combinationKey);

      // Skip if row already exists
      if (rowExists(parsed.eventId, parsed.pixelId)) {
        autoCreatedCombinationsRef.current.add(combinationKey);
        return;
      }

      // Mark as auto-created
      autoCreatedCombinationsRef.current.add(combinationKey);

      // Find pixel information
      const { pixelId, pixelName } = findPixelForEvent(parsed.eventId, parsed.pixelId);

      combinationsToCreate.push({
        pixelId,
        pixelName,
        eventId: parsed.eventId,
        eventName: parsed.eventName,
        operator: defaultOperator,
        threshold: defaultThreshold,
        combinationKey,
      });
    });

    if (combinationsToCreate.length === 0) return;

    // Build a map of rows that have a pixel selected but no event
    const pixelOnlyRowsMap = brandConfig.thresholds.reduce<Map<string, number[]>>(
      (map, threshold, index) => {
        const hasPixel = threshold.pixelId && threshold.pixelId.trim() !== '';
        const missingEvent =
          !threshold.eventId && (!threshold.eventName || threshold.eventName.trim() === '');

        if (hasPixel && missingEvent && threshold.pixelId) {
          const rows = map.get(threshold.pixelId) || [];
          rows.push(index);
          map.set(threshold.pixelId, rows);
        }

        return map;
      },
      new Map()
    );

    // Try to fill rows that already have a pixel selected first
    const combinationsAfterPixelFill: RowCreationData[] = [];

    combinationsToCreate.forEach(combination => {
      if (combination.pixelId) {
        const pixelRows = pixelOnlyRowsMap.get(combination.pixelId);
        if (pixelRows && pixelRows.length > 0) {
          const rowIndex = pixelRows.shift();
          if (rowIndex !== undefined) {
            fillEmptyRow(combination, rowIndex);
            return;
          }
        }
      }

      combinationsAfterPixelFill.push(combination);
    });

    // Find existing completely empty rows
    const emptyRows = brandConfig.thresholds
      .map((threshold, index) => ({ threshold, index }))
      .filter(
        ({ threshold }) =>
          (!threshold.pixelId || threshold.pixelId.trim() === '') &&
          !threshold.eventId &&
          (!threshold.eventName || threshold.eventName.trim() === '')
      );

    // Fill existing empty rows
    const combinationsToFillExisting = combinationsAfterPixelFill.slice(0, emptyRows.length);
    const combinationsToCreateNew = combinationsAfterPixelFill.slice(emptyRows.length);

    combinationsToFillExisting.forEach((combination, idx) => {
      if (idx < emptyRows.length) {
        fillEmptyRow(combination, emptyRows[idx].index);
      }
    });

    // Create new rows for remaining combinations
    if (combinationsToCreateNew.length > 0) {
      thresholdCountBeforeCreationRef.current = brandConfig.thresholds.length;
      pendingCreationsRef.current = combinationsToCreateNew;

      combinationsToCreateNew.forEach(() => {
        onAddThresholdRow(brandConfig.brandId);
      });
    }
  }, [
    brandConfig,
    selectedEvents,
    rowExists,
    findPixelForEvent,
    fillEmptyRow,
    onAddThresholdRow,
    defaultOperator,
    defaultThreshold,
  ]);

  /**
   * Process pending row creations after new rows are added
   */
  useEffect(() => {
    const pending = pendingCreationsRef.current;
    const thresholdCountBefore = thresholdCountBeforeCreationRef.current;

    if (pending.length > 0 && brandConfig.thresholds.length > thresholdCountBefore) {
      // Find newly created empty thresholds
      const newlyCreatedEmptyThresholds = brandConfig.thresholds
        .map((threshold, index) => ({ threshold, index }))
        .filter(
          ({ threshold, index }) =>
            index >= thresholdCountBefore && !threshold.pixelId && !threshold.eventId
        );

      if (newlyCreatedEmptyThresholds.length >= pending.length) {
        // Clear pending creations immediately
        pendingCreationsRef.current = [];
        thresholdCountBeforeCreationRef.current = 0;

        // Populate the newly created rows
        pending.forEach((pendingCreation, idx) => {
          if (idx < newlyCreatedEmptyThresholds.length) {
            fillEmptyRow(pendingCreation, newlyCreatedEmptyThresholds[idx].index);
          }
        });
      }
    }
  }, [brandConfig.thresholds, fillEmptyRow]);

  /**
   * Main effect to handle event selection changes
   */
  useEffect(() => {
    const prevEvents = prevSelectedEventsRef.current;
    const currentEvents = selectedEvents;

    // Find removed events
    const removedEvents = prevEvents.filter(event => !currentEvents.includes(event));

    // Remove auto-created rows for removed events
    if (removedEvents.length > 0) {
      removeAutoCreatedRows(removedEvents);
    }

    // Update ref for next comparison
    prevSelectedEventsRef.current = currentEvents;

    // Create rows for selected events
    createRowsForEvents();

    // Clear tracking when all events are deselected
    if (currentEvents.length === 0) {
      const allAutoCreatedIds = Array.from(autoCreatedThresholdsRef.current.values()).flat();
      const indicesToRemove = allAutoCreatedIds
        .map(thresholdId => brandConfig.thresholds.findIndex(t => t.id === thresholdId))
        .filter(index => index !== -1)
        .sort((a, b) => b - a);

      indicesToRemove.forEach(thresholdIndex => {
        onRemoveThresholdRow(brandConfig.brandId, thresholdIndex);
      });

      autoCreatedThresholdsRef.current.clear();
      autoCreatedCombinationsRef.current.clear();
    }
  }, [
    brandConfig.brandId,
    brandConfig.thresholds,
    createRowsForEvents,
    onRemoveThresholdRow,
    removeAutoCreatedRows,
    selectedEvents,
  ]);

  /**
   * Cleanup tracking when threshold is manually deleted
   */
  const cleanupDeletedThreshold = useCallback((thresholdId: string) => {
    // Find and remove from tracking maps
    autoCreatedThresholdsRef.current.forEach((thresholdIds, combinationKey) => {
      const updatedIds = thresholdIds.filter(id => id !== thresholdId);
      if (updatedIds.length === 0) {
        autoCreatedThresholdsRef.current.delete(combinationKey);
        autoCreatedCombinationsRef.current.delete(combinationKey);
      } else {
        autoCreatedThresholdsRef.current.set(combinationKey, updatedIds);
      }
    });
  }, []);

  return {
    cleanupDeletedThreshold,
  };
};
